import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  TablePagination,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  InputAdornment,
  Alert,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import { hasWritePermission } from '../../utils/menuFilter';
import {
  Search,
  Refresh,
  Visibility,
  CheckCircle,
  Cancel,
  Download,
  Receipt,
  Person,
  Warning,
  CheckCircleOutline,
  Business,
  AttachMoney,
  Info,
} from '@mui/icons-material';

const PlotCancellationRequest = () => {
  const canWriteCancellation = hasWritePermission('plot-booking.plot-cancellation-request');
  const [searchForm, setSearchForm] = useState({
    bookingId: '',
    status: '',
    fromDate: '',
    toDate: '',
  });

  const [showRequestList, setShowRequestList] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tableSearchQuery, setTableSearchQuery] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');

  // Sample cancellation request data
  const [requestData] = useState([
    {
      id: 1,
      bookingId: 'BK001',
      requestBy: 'John Smith',
      reason: 'Financial difficulties due to job loss',
      requestDate: '2025-10-01',
      document: 'cancellation_request_BK001.pdf',
      status: 'Pending',
      customerMobile: '9876543210',
      plotNo: 'P101',
      siteName: 'Green Valley',
      requestDetails: 'Customer facing financial constraints and unable to continue with the payment plan.',
      totalAmount: '₹54,00,000',
      paidAmount: '₹5,40,000',
      balanceAmount: '₹48,60,000',
    },
    {
      id: 2,
      bookingId: 'BK002',
      requestBy: 'Sarah Johnson',
      reason: 'Change of location due to job transfer',
      requestDate: '2025-10-05',
      document: 'cancellation_request_BK002.pdf',
      status: 'Approved',
      customerMobile: '9876543211',
      plotNo: 'P205',
      siteName: 'Hill View',
      requestDetails: 'Customer got transferred to another city and cannot proceed with the booking.',
      totalAmount: '₹81,90,000',
      paidAmount: '₹20,47,500',
      balanceAmount: '₹61,42,500',
    },
    {
      id: 3,
      bookingId: 'BK003',
      requestBy: 'Michael Brown',
      reason: 'Medical emergency requiring funds',
      requestDate: '2025-10-07',
      document: 'cancellation_request_BK003.pdf',
      status: 'Rejected',
      customerMobile: '9876543212',
      plotNo: 'P321',
      siteName: 'Paradise Heights',
      requestDetails: 'Customer needs funds for medical emergency but does not meet cancellation criteria.',
      totalAmount: '₹1,20,00,000',
      paidAmount: '₹12,00,000',
      balanceAmount: '₹1,08,00,000',
    },
    {
      id: 4,
      bookingId: 'BK004',
      requestBy: 'Emily Davis',
      reason: 'Property defects and builder issues',
      requestDate: '2025-10-08',
      document: 'cancellation_request_BK004.pdf',
      status: 'Under Review',
      customerMobile: '9876543213',
      plotNo: 'P456',
      siteName: 'Royal Gardens',
      requestDetails: 'Customer reporting multiple issues with property development and builder services.',
      totalAmount: '₹1,85,62,500',
      paidAmount: '₹37,12,500',
      balanceAmount: '₹1,48,50,000',
    },
    {
      id: 5,
      bookingId: 'BK005',
      requestBy: 'David Wilson',
      reason: 'Dissatisfied with amenities provided',
      requestDate: '2025-10-09',
      document: 'cancellation_request_BK005.pdf',
      status: 'Pending',
      customerMobile: '9876543214',
      plotNo: 'P789',
      siteName: 'Green Valley',
      requestDetails: 'Customer not satisfied with the amenities and infrastructure development progress.',
      totalAmount: '₹73,72,800',
      paidAmount: '₹7,37,280',
      balanceAmount: '₹66,35,520',
    },
    {
      id: 6,
      bookingId: 'BK006',
      requestBy: 'Lisa Anderson',
      reason: 'Legal disputes with documentation',
      requestDate: '2025-09-28',
      document: 'cancellation_request_BK006.pdf',
      status: 'Approved',
      customerMobile: '9876543215',
      plotNo: 'P654',
      siteName: 'Hill View',
      requestDetails: 'Documentation issues resolved and cancellation approved with partial refund.',
      totalAmount: '₹1,02,75,200',
      paidAmount: '₹25,68,800',
      balanceAmount: '₹77,06,400',
    },
    {
      id: 7,
      bookingId: 'BK007',
      requestBy: 'Robert Taylor',
      reason: 'Investment strategy change',
      requestDate: '2025-09-25',
      document: 'cancellation_request_BK007.pdf',
      status: 'Rejected',
      customerMobile: '9876543216',
      plotNo: 'P987',
      siteName: 'Paradise Heights',
      requestDetails: 'Customer wants to change investment strategy but request denied due to policy violations.',
      totalAmount: '₹95,00,000',
      paidAmount: '₹19,00,000',
      balanceAmount: '₹76,00,000',
    },
  ]);

  const statusOptions = ['Pending', 'Under Review', 'Approved', 'Rejected'];

  const handleInputChange = (field, value) => {
    setSearchForm({
      ...searchForm,
      [field]: value,
    });
  };

  const handleSearch = async () => {
    setSearchLoading(true);

    // Simulate API call
    setTimeout(() => {
      setShowRequestList(true);
      setPage(0);
      console.log('Searching with criteria:', searchForm);
      setSearchLoading(false);
    }, 1500);
  };

  const handleReset = () => {
    setSearchForm({
      bookingId: '',
      status: '',
      fromDate: '',
      toDate: '',
    });
    setTableSearchQuery('');
    setShowRequestList(true);
    setPage(0);
    setSuccessMessage('');
  };

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
    // Filter by status based on tab
    const statusMap = { 0: '', 1: 'Pending', 2: 'Under Review', 3: 'Approved', 4: 'Rejected' };
    setSearchForm({
      ...searchForm,
      status: statusMap[newValue],
    });
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setViewDialogOpen(true);
  };

  const handleDownloadDocument = (documentName) => {
    console.log('Downloading document:', documentName);
    // Implement document download logic here
  };

  const handleApproveRequest = (requestId) => {
    setSuccessMessage(`Cancellation request ${requestId} has been approved successfully!`);
    console.log('Approving request:', requestId);
    // Implement approval logic here
  };

  const handleRejectRequest = (requestId) => {
    setSuccessMessage(`Cancellation request ${requestId} has been rejected.`);
    console.log('Rejecting request:', requestId);
    // Implement rejection logic here
  };

  // Calculate statistics
  const totalRequests = requestData.length;
  const pendingCount = requestData.filter(request => request.status === 'Pending').length;
  const underReviewCount = requestData.filter(request => request.status === 'Under Review').length;
  const approvedCount = requestData.filter(request => request.status === 'Approved').length;
  const rejectedCount = requestData.filter(request => request.status === 'Rejected').length;

  const getFilteredByStatus = (status) => {
    return getFilteredRequestData().filter(request => !status || request.status === status);
  };

  const getFilteredRequestData = () => {
    if (!showRequestList) return [];

    let filteredData = requestData;

    // Filter by search form criteria
    if (searchForm.bookingId) {
      filteredData = filteredData.filter(request =>
        request.bookingId.toLowerCase().includes(searchForm.bookingId.toLowerCase())
      );
    }
    if (searchForm.status) {
      filteredData = filteredData.filter(request => request.status === searchForm.status);
    }
    if (searchForm.fromDate) {
      filteredData = filteredData.filter(request =>
        new Date(request.requestDate) >= new Date(searchForm.fromDate)
      );
    }
    if (searchForm.toDate) {
      filteredData = filteredData.filter(request =>
        new Date(request.requestDate) <= new Date(searchForm.toDate)
      );
    }

    // Filter by table search query
    if (tableSearchQuery.trim()) {
      filteredData = filteredData.filter(request =>
        Object.values(request).some(value =>
          String(value).toLowerCase().includes(tableSearchQuery.toLowerCase())
        )
      );
    }

    return filteredData;
  };

  const getSearchSummary = () => {
    const criteria = [];
    if (searchForm.bookingId) criteria.push(`Booking ID: "${searchForm.bookingId}"`);
    if (searchForm.status) criteria.push(`Status: "${searchForm.status}"`);
    if (searchForm.fromDate) criteria.push(`From: ${searchForm.fromDate}`);
    if (searchForm.toDate) criteria.push(`To: ${searchForm.toDate}`);

    return criteria.length > 0 ? criteria.join(', ') : 'All Cancellation Requests';
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Pending':
        return {
          backgroundColor: '#ff9800',
          color: 'white',
        };
      case 'Under Review':
        return {
          backgroundColor: '#2196f3',
          color: 'white',
        };
      case 'Approved':
        return {
          backgroundColor: '#4caf50',
          color: 'white',
        };
      case 'Rejected':
        return {
          backgroundColor: '#f44336',
          color: 'white',
        };
      default:
        return {};
    }
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#fafafa', minHeight: 'auto' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
          Cancellation Request Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage and review plot booking cancellation requests
        </Typography>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 4, borderRadius: 2 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircleOutline />
            {successMessage}
          </Box>
        </Alert>
      )}

      {/* Summary Cards */}
      {/* <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={2.4}>
          <Card sx={{ p: 3, backgroundColor: '#e3f2fd', border: '1px solid #90caf9' }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Receipt sx={{ color: '#1976d2', fontSize: 32 }} />
              <Box>
                <Typography variant="body2" color="text.secondary">Total Requests</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2' }}>
                  {totalRequests}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card sx={{ p: 3, backgroundColor: '#fff3e0', border: '1px solid #ffcc02' }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Warning sx={{ color: '#f57c00', fontSize: 32 }} />
              <Box>
                <Typography variant="body2" color="text.secondary">Pending</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#f57c00' }}>
                  {pendingCount}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card sx={{ p: 3, backgroundColor: '#e1f5fe', border: '1px solid #81d4fa' }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Info sx={{ color: '#0288d1', fontSize: 32 }} />
              <Box>
                <Typography variant="body2" color="text.secondary">Under Review</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#0288d1' }}>
                  {underReviewCount}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card sx={{ p: 3, backgroundColor: '#e8f5e9', border: '1px solid #c8e6c9' }}>
            <Box display="flex" alignItems="center" gap={2}>
              <CheckCircle sx={{ color: '#2e7d32', fontSize: 32 }} />
              <Box>
                <Typography variant="body2" color="text.secondary">Approved</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                  {approvedCount}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card sx={{ p: 3, backgroundColor: '#ffebee', border: '1px solid #ffcdd2' }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Cancel sx={{ color: '#d32f2f', fontSize: 32 }} />
              <Box>
                <Typography variant="body2" color="text.secondary">Rejected</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#d32f2f' }}>
                  {rejectedCount}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid> */}

      {/* Search Section */}
      <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Search sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
              Search Cancellation Requests
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <TextField
                label="Booking ID"
                variant="outlined"
                fullWidth
                value={searchForm.bookingId}
                onChange={(e) => handleInputChange('bookingId', e.target.value)}
                placeholder="Enter Booking ID"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Receipt color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={searchForm.status}
                  label="Status"
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  {statusOptions.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label="From Date"
                type="date"
                variant="outlined"
                fullWidth
                value={searchForm.fromDate}
                onChange={(e) => handleInputChange('fromDate', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                label="To Date"
                type="date"
                variant="outlined"
                fullWidth
                value={searchForm.toDate}
                onChange={(e) => handleInputChange('toDate', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSearch}
                  disabled={searchLoading}
                  startIcon={<Search />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '15px',
                    boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                    borderRadius: 2
                  }}
                >
                  {searchLoading ? 'Searching...' : 'Search Requests'}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleReset}
                  startIcon={<Refresh />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '15px',
                    borderRadius: 2
                  }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tab Navigation */}
      <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} sx={{ px: 3 }}>
            <Tab label={`All Requests (${getFilteredByStatus('').length})`} />
            <Tab label={`Pending (${getFilteredByStatus('Pending').length})`} />
            <Tab label={`Under Review (${getFilteredByStatus('Under Review').length})`} />
            <Tab label={`Approved (${getFilteredByStatus('Approved').length})`} />
            <Tab label={`Rejected (${getFilteredByStatus('Rejected').length})`} />
          </Tabs>
        </Box>
      </Card>

      {/* Cancellation Requests Table */}
      {showRequestList && (
        <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box display="flex" alignItems="center" gap={2}>
                  <Receipt sx={{ color: '#1976d2', fontSize: 24 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Cancellation Request List
                  </Typography>
                </Box>
                <TextField
                  label="Search Requests..."
                  variant="outlined"
                  size="small"
                  value={tableSearchQuery}
                  onChange={(e) => setTableSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 250, borderRadius: 2 }}
                />
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Search Criteria: {getSearchSummary()}
              </Typography>
              <Typography variant="body2" color="primary" sx={{ mt: 1, fontWeight: 600 }}>
                Found {getFilteredRequestData().length} cancellation request(s)
              </Typography>
            </Box>

            {getFilteredRequestData().length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>S.No</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Request Info</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Customer Details</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Reason & Date</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Document</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#1976d2' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getFilteredRequestData()
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((request, index) => (
                        <TableRow key={request.id} hover>
                          <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                {request.bookingId}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {request.plotNo} • {request.siteName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {request.requestBy}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {request.customerMobile}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  maxWidth: 200,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {request.reason}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {request.requestDate}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleDownloadDocument(request.document)}
                              startIcon={<Download />}
                              sx={{
                                fontSize: '0.75rem',
                                py: 0.5,
                                textTransform: 'none',
                                borderRadius: 2
                              }}
                            >
                              Download
                            </Button>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={request.status}
                              sx={getStatusStyle(request.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="View Details">
                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => handleViewRequest(request)}
                                >
                                  <Visibility />
                                </IconButton>
                              </Tooltip>

                              {request.status === 'Pending' && (
                                <>
                                  <Tooltip title="Approve Request">
                                    <IconButton
                                      color="success"
                                      size="small"
                                      onClick={() => handleApproveRequest(request.id)}
                                    >
                                      <CheckCircle />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Reject Request">
                                    <IconButton
                                      color="error"
                                      size="small"
                                      onClick={() => handleRejectRequest(request.id)}
                                    >
                                      <Cancel />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <Alert severity="warning" sx={{ mx: 3, mb: 3, borderRadius: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    No Cancellation Requests Found
                  </Typography>
                  <Typography variant="body2">
                    No cancellation requests match your search criteria: {getSearchSummary()}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Please try different search criteria.
                  </Typography>
                </Alert>
              </Box>
            )}

            {/* Pagination */}
            <TablePagination
              
              component="div"
              count={getFilteredRequestData().length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ px: 3, py: 2 }}
            />
          </CardContent>
        </Card>
      )}

      {/* View Request Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{
          fontSize: '24px',
          fontWeight: 700,
          color: '#000',
          borderBottom: '1px solid #e0e0e0',
          pb: 2
        }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Receipt sx={{ color: '#1976d2', fontSize: 28 }} />
            Cancellation Request Details
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {selectedRequest && (
            <Box sx={{ mt: 2 }}>
              {/* Booking Information Card */}
              <Card sx={{ p: 3, mb: 3, backgroundColor: '#e3f2fd', border: '1px solid #90caf9' }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Business sx={{ color: '#1976d2', fontSize: 20 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    Booking Information
                  </Typography>
                </Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">Booking ID</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}>
                      {selectedRequest.bookingId}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Plot Number</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                      {selectedRequest.plotNo}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Site Name</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedRequest.siteName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">Customer Name</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                      {selectedRequest.requestBy}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Mobile Number</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedRequest.customerMobile}
                    </Typography>
                  </Grid>
                </Grid>
              </Card>

              {/* Financial Summary Card */}
              <Card sx={{ p: 3, mb: 3, backgroundColor: '#e8f5e9', border: '1px solid #c8e6c9' }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <AttachMoney sx={{ color: '#2e7d32', fontSize: 20 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                    Financial Summary
                  </Typography>
                </Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                      {selectedRequest.totalAmount}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">Paid Amount</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                      {selectedRequest.paidAmount}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">Balance Amount</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#d32f2f' }}>
                      {selectedRequest.balanceAmount}
                    </Typography>
                  </Grid>
                </Grid>
              </Card>

              {/* Request Details Card */}
              <Card sx={{ p: 3, backgroundColor: '#fff3e0', border: '1px solid #ffcc02' }}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Warning sx={{ color: '#f57c00', fontSize: 20 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#f57c00' }}>
                    Cancellation Request Information
                  </Typography>
                </Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">Request Date</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                      {selectedRequest.requestDate}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">Status</Typography>
                    <Chip
                      label={selectedRequest.status}
                      sx={getStatusStyle(selectedRequest.status)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">Document</Typography>
                    <Button
                      variant="outlined"
                      onClick={() => handleDownloadDocument(selectedRequest.document)}
                      startIcon={<Download />}
                      sx={{
                        mt: 1,
                        textTransform: 'none',
                        borderRadius: 2
                      }}
                    >
                      {selectedRequest.document}
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Cancellation Reason</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                      {selectedRequest.reason}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">Request Details</Typography>
                    <Typography variant="body1">
                      {selectedRequest.requestDetails}
                    </Typography>
                  </Grid>
                </Grid>
              </Card>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button
            onClick={() => setViewDialogOpen(false)}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlotCancellationRequest;